/**
 * The report's visual vocabulary, in one place.
 *
 * Every ramp below was run through the data-viz validator against this app's
 * actual chart surface (#ffffff), not a assumed one. Do not hand-edit a hex
 * here: the ordinal ramps pass a lightness-gap and light-end-contrast gate that
 * is not obvious by eye, and nudging one step silently breaks it.
 *
 *   ordinal (5): #86b6ef #5598e7 #2a78d6 #1c5cab #104281  → all checks pass
 *   ordinal (4): the first four of the same                → all checks pass
 *
 * Five is the ceiling on white: a sixth step fails the adjacent-lightness gate,
 * which is part of why the pipeline is modelled as five stages.
 */

// Ordered light→dark. Position in the pipeline picks the step, so a stage keeps
// its colour when a filter changes which stages have rows.
export const PIPELINE_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];

// Waiting-time buckets. Ordinal, not status: the four status colours fail the
// series separation floor when used as adjacent bars (warning vs serious is
// ΔE 13.6, under the 15 floor), so the distribution is encoded by depth and the
// alarm is carried by a status tile instead.
export const AGE_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab'];

// Single hue for the one-series department chart.
export const SERIES = '#2a78d6';

// Two-series pair for the throughput line: categorical slots 1 and 2, validated
// as an adjacent pair against this app's white surface (CVD ΔE 24.7, normal
// vision 33.6 — clear of both floors). Two series means a legend is mandatory;
// the single-series bar charts correctly have none, and that asymmetry is
// deliberate.
export const SERIES_PAIR = ['#2a78d6', '#eb6834'];

// Continuous magnitude for the cross-tab. Sequential encoding is allowed to let
// its lightest step recede toward the surface — that is what "near zero" means.
export const SEQUENTIAL = [
  '#eef5fe', '#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b',
];

// Reserved. Only ever on a stat tile, always beside an icon and a label — never
// as a series colour.
export const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  critical: '#d03b3b',
};

export const INK = {
  primary: '#111827',
  secondary: '#6b7280',
  muted: '#898781',
  grid: '#e1e0d9',
  baseline: '#c3c2b7',
  border: '#e5e7eb',
  surface: '#ffffff',
  plane: '#f9fafb',
};

export const FONT = { fontFamily: 'Roboto, Arial, sans-serif' };

/**
 * Ink for text placed on top of a filled mark.
 *
 * The one place text may sit inside a data colour — so it has to clear contrast
 * against whatever step it lands on, which changes per cell in a heat grid.
 */
export const inkOn = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  // Rec. 601 luma is good enough to pick a side; the ramps are far from the
  // boundary in both directions.
  const luma = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return luma > 0.6 ? INK.primary : '#ffffff';
};
